import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, AlertCircle, LogOut } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isOAuthUser, getPlatform } from '../../services/api';
import './Alert.css';

const defaultState = {
  show: false,
  type: '',
  message: '',
  loginButton: false,
  confirmLabel: '확인',
  cancelable: false,
  dismissable: true,
  onConfirm: null,
};

// ===== useAlert 훅 =====
export const useAlert = () => {
  const [alert, setAlert] = useState(defaultState);

  const showAlert = (type, message, options = {}) => {
    const {
      loginButton = false,
      autoClose = true,
      confirmLabel = '확인',
      cancelable = false,
      dismissable = true,
      onConfirm = null,
    } = options;

    setAlert({ show: true, type, message, loginButton, confirmLabel, cancelable, dismissable, onConfirm });

    if (autoClose) {
      setTimeout(() => setAlert(defaultState), 3000);
    }
  };

  const closeAlert = () => setAlert(defaultState);

  // refreshToken 기준으로 OAuth 가입자 확인 후 게스트일 때만 alert 표시
  const showAuthAlert = () => {
    if (isOAuthUser()) return;

    const platform = getPlatform();
    if (platform === 'ios' || platform === 'android') {
      showAlert('error', '이용하려면 회원가입이 필요해요.', { loginButton: true, autoClose: false });
    } else {
      showAlert('info', '앱에서 시도해주세요.', { autoClose: true });
    }
  };

  return { alert, showAlert, closeAlert, showAuthAlert };
};

// ===== AlertModal 컴포넌트 =====
const AlertModal = ({ alert, onClose }) => {
  const navigate = useNavigate();

  const handleConfirm = () => {
    onClose();
    alert.onConfirm?.();
  };

  const renderIcon = () => {
    if (alert.type === 'success') return <CheckCircle size={30} />;
    if (alert.type === 'logout') return <LogOut size={30} />;
    return <AlertCircle size={30} />;
  };

  const location = useLocation();
  const isCoinPath = /coinMain|coinDetail/i.test(location.pathname);

  return (
    <AnimatePresence>
      {alert.show && (
        <motion.div
          className="alert-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={alert.dismissable !== false ? onClose : undefined}
        >
          <motion.div
            className="alert-sheet"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
          >
            <div className={`alert-icon-wrap ${alert.type}`}>
              {renderIcon()}
            </div>

            <p className="alert-message">{alert.message}</p>

            {alert.loginButton ? (
              <>
                 {isCoinPath ? (
                    <>
                      {/* 1. 코인 경로일 때: 로그인 버튼 + 취소 버튼(ghost) */}
                      <button
                        className="alert-cta-btn primary"
                        onClick={() => { onClose(); navigate('/profile'); }}
                      >
                        로그인 하기
                      </button>
                      <button className="alert-cta-btn ghost" onClick={onClose}>
                        취소
                      </button>
                    </>
                  ) : (
                    <>
                      {/* 2. 그 외의 경로일 때: 안내 문구 + 확인 버튼(primary) */}
                      <div className="alert-message">
                        프로필에서 회원가입을 진행해주세요
                      </div>
                      <button className="alert-cta-btn primary" onClick={onClose}>
                        확인
                      </button>
                    </>
                  )}
              </>
            ) : (
              <>
                <button
                  className={`alert-cta-btn ${alert.type === 'logout' ? 'danger' : 'primary'}`}
                  onClick={handleConfirm}
                >
                  {alert.confirmLabel}
                </button>
                {alert.cancelable && (
                  <button className="alert-cta-btn ghost" onClick={onClose}>
                    취소
                  </button>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AlertModal;
